import { Divider, Group, Select } from '@byecode/ui'
import type { TypeInstanceMap, UpdateQueryParameterAction } from '@lighthouse/core'
import type { NodeTypes } from '@lighthouse/shared'
import { FieldTypeTag, ListItem4ByecodeUi, nodeTypeOptions, useAtomData } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom, pageQueryParameterFamily } from '@/atoms/page/state'
import { ArgInput } from '@/components/ArgInput'

import * as SC from '../styles'

interface UpdateQueryParameterActionConfigureProps {
    nodeType?: NodeTypes
    actionTypeSwitcher?: React.ReactNode
    allParentNodes?: import('@lighthouse/shared').FlowNode[]
    allParentActions?: import('@lighthouse/core').IndependentAction[]
    value: UpdateQueryParameterAction['data']['updateQueryParameter']
    onChange: (value: UpdateQueryParameterAction['data']['updateQueryParameter']) => void
}

export const UpdateQueryParameterActionConfigure: React.FC<UpdateQueryParameterActionConfigureProps> = ({
    actionTypeSwitcher,
    nodeType,
    allParentNodes,
    allParentActions,
    value,
    onChange
}) => {
    const { pageId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(
            s => ({
                pageId: s?.pageId ?? ''
            }),
            []
        )
    )
    const { queryParameterId } = value
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))

    const currentQueryParameter = useMemo(() => {
        return pageQueryParameter?.find(item => item.id === queryParameterId)
    }, [pageQueryParameter, queryParameterId])

    const options = useMemo(() => {
        if (!pageQueryParameter) {
            return []
        }
        return pageQueryParameter.map(item => ({
            label: item.name,
            value: item.id,
            type: item.innerType,
            extra: <FieldTypeTag innerType={item.innerType} />
        }))
    }, [pageQueryParameter])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>目标更新查询参数</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select
                            searchable
                            options={options}
                            styles={{
                                root: {
                                    alignItems: 'center'
                                },
                                prefix: {
                                    lineHeight: 'normal',
                                    height: 20,
                                    margin: '0 4px'
                                }
                            }}
                            prefix={
                                queryParameterId && (
                                    <FieldTypeTag
                                        innerType={options.find(item => item.value === queryParameterId)?.type}
                                    />
                                )
                            }
                            value={queryParameterId}
                            onChange={val => {
                                onChange({ ...value, queryParameterId: val })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            {queryParameterId && currentQueryParameter && (
                <>
                    <Divider />
                    <Group label="查询参数更新为">
                        <ListItem4ByecodeUi>
                            <SC.FormItemContent style={{ flex: 1 }}>
                                <ArgInput
                                    autoHeight
                                    disabledLabel
                                    data={currentQueryParameter}
                                    allParentNodes={allParentNodes}
                                    allParentActions={allParentActions}
                                    value={value.value}
                                    onChange={v => {
                                        onChange({ ...value, value: v })
                                    }}
                                />
                            </SC.FormItemContent>
                        </ListItem4ByecodeUi>
                    </Group>
                </>
            )}
        </SC.Container>
    )
}
